import React, { useState, useEffect } from 'react';
import {
  Box,
  Card,
  CardContent,
  Typography,
  Grid,
  Button,
  Chip,
  Alert,
  CircularProgress,
  IconButton,
  Tooltip,
} from '@mui/material';
import {
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Refresh as RefreshIcon,
  OpenInNew as OpenInNewIcon,
} from '@mui/icons-material';
import { tagopsService } from '../services/tagopsService';
import { useNotifications, createErrorNotification } from './common/NotificationSystem';
import { isAwsMarketplaceSubscription } from '../utils/subscriptionUtils';

const AWS_MARKETPLACE_SUBSCRIBE_URL = 'https://aws.amazon.com/marketplace/procurement?productId=prod-zvt2eyek6y5ns';
const AWS_MARKETPLACE_MANAGE_URL = 'https://console.aws.amazon.com/marketplace/home#/subscriptions';

interface Subscription {
  tenant_id: string;
  subscription_id: string;
  plan_id: string;
  status: string;
  created_at: string;
  activated_at?: string;
  tier_type?: string;
  aws_account_count?: number | string;
  quantity?: number | string;
  is_free_trial_offer?: boolean;
}

const parseAccountCount = (value: number | string | undefined, fallback = 1): number => {
  if (value === undefined || value === '') {
    return fallback;
  }
  const parsed = typeof value === 'string' ? parseInt(value, 10) : value;
  return Number.isNaN(parsed) ? fallback : parsed;
};

const formatAccountLimit = (count: number): string => {
  if (count === -1) {
    return 'Unlimited AWS accounts';
  }
  return `${count} AWS ${count === 1 ? 'Account' : 'Accounts'}`;
};

export const Subscription: React.FC = () => {
  const { showNotification } = useNotifications();
  const [currentSubscription, setCurrentSubscription] = useState<Subscription | null>(null);
  const [loading, setLoading] = useState(true);
  const [trialEndDate, setTrialEndDate] = useState<string | null>(null);
  const [currentOnboardedAccounts, setCurrentOnboardedAccounts] = useState<number>(0);

  useEffect(() => {
    loadSubscriptionData();
    loadAccountCount();
  }, []);

  const loadAccountCount = async () => {
    try {
      const response = await tagopsService.getAccountCount();
      setCurrentOnboardedAccounts(response.count);
    } catch (error) {
      console.error('Error loading account count:', error);
    }
  };

  const loadSubscriptionData = async () => {
    try {
      setLoading(true);
      const subscriptionResponse = await tagopsService.getSubscriptionStatus();
      setCurrentSubscription(subscriptionResponse.subscription);
      setTrialEndDate(subscriptionResponse.trial_end_date || null);
    } catch (error) {
      console.error('Error loading subscription data:', error);
      showNotification(createErrorNotification(
        new Error('Failed to load subscription data'),
        loadSubscriptionData
      ));
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return 'success';
      case 'PENDING':
        return 'warning';
      case 'CANCELLED':
      case 'SUSPENDED':
        return 'error';
      default:
        return 'default';
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status?.toUpperCase()) {
      case 'ACTIVE':
        return <CheckCircleIcon />;
      case 'PENDING':
        return <WarningIcon />;
      case 'CANCELLED':
      case 'SUSPENDED':
        return <CancelIcon />;
      default:
        return <InfoIcon />;
    }
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    });
  };

  const formatDateTime = (dateString: string) => {
    return new Date(dateString).toLocaleString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      timeZoneName: 'short'
    });
  };

  const formatTrialRemaining = (endDate: string) => {
    const trialEnd = new Date(endDate);
    const now = new Date();
    const diffDays = Math.ceil((trialEnd.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));
    return `${diffDays > 0 ? diffDays : 0} ${diffDays === 1 ? 'day' : 'days'} remaining`;
  };

  const isFixedPlan = currentSubscription && (currentSubscription.plan_id === 'fixed' || currentSubscription.tier_type === 'Fixed');
  const isMarketplacePlan = isAwsMarketplaceSubscription(currentSubscription);
  const accountLimit = currentSubscription
    ? parseAccountCount(currentSubscription.aws_account_count ?? currentSubscription.quantity, isMarketplacePlan ? -1 : 1)
    : null;

  const getPlanDisplayName = (subscription: Subscription) => {
    if (isAwsMarketplaceSubscription(subscription)) {
      if (subscription.is_free_trial_offer) {
        return 'AWS Marketplace (Free Trial)';
      }
      return 'AWS Marketplace';
    }
    if (subscription.plan_id === 'fixed' || subscription.tier_type === 'Fixed') {
      const count = parseAccountCount(subscription.aws_account_count || subscription.quantity, 1);
      return `Fixed (${formatAccountLimit(count)} paid for)`;
    }
    return subscription.plan_id;
  };

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4 }}>
        <Box>
          <Typography variant="h4" sx={{ fontWeight: 700, mb: 1 }}>
            Subscription
          </Typography>
          <Typography variant="h6" color="text.secondary">
            View your TagOps subscription status and account limits
          </Typography>
        </Box>
        <Tooltip title="Refresh subscription data">
          <IconButton onClick={loadSubscriptionData} color="primary">
            <RefreshIcon />
          </IconButton>
        </Tooltip>
      </Box>

      {currentSubscription ? (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Current Subscription
              </Typography>
              <Chip
                icon={getStatusIcon(currentSubscription.status)}
                label={currentSubscription.status}
                color={getStatusColor(currentSubscription.status) as any}
                variant="outlined"
              />
            </Box>

            <Grid container spacing={3}>
              <Grid item xs={12} md={6}>
                <Box sx={{ mb: 2 }}>
                  <Typography variant="body2" color="text.secondary">
                    Plan
                  </Typography>
                  <Typography variant="h6">
                    {getPlanDisplayName(currentSubscription)}
                  </Typography>
                </Box>
              </Grid>

              <Grid item xs={12} md={6}>
                {!isFixedPlan && currentSubscription.activated_at && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Activated
                    </Typography>
                    <Typography variant="body2">
                      {formatDate(currentSubscription.activated_at)}
                    </Typography>
                  </Box>
                )}

                {trialEndDate && currentSubscription.is_free_trial_offer && (
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Free Trial Expires
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 500 }}>
                      {formatDateTime(trialEndDate)}
                    </Typography>
                    <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main', mt: 0.5 }}>
                      {formatTrialRemaining(trialEndDate)}
                    </Typography>
                  </Box>
                )}
              </Grid>
            </Grid>

            {isMarketplacePlan && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Account limit:</strong> {formatAccountLimit(accountLimit ?? -1)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Onboarded: {currentOnboardedAccounts}
                    {accountLimit !== null && accountLimit !== -1 ? ` of ${accountLimit}` : ''} AWS {currentOnboardedAccounts === 1 ? 'Account' : 'Accounts'}.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Billing is managed in AWS Marketplace. Subscribe, change, or cancel your plan there.
                  </Typography>
                </Alert>
              </Box>
            )}

            {isFixedPlan && (
              <Box sx={{ mt: 3 }}>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Accounts paid for:</strong> {formatAccountLimit(accountLimit ?? 1)}
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Onboarded: {currentOnboardedAccounts} of {accountLimit ?? 1} AWS {(accountLimit ?? 1) === 1 ? 'Account' : 'Accounts'}.
                  </Typography>
                  <Typography variant="body2" sx={{ mt: 1 }}>
                    Changes to your Fixed plan are managed by your administrator. Contact support to change the number of accounts.
                  </Typography>
                </Alert>
              </Box>
            )}
          </CardContent>
        </Card>
      ) : (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                Current Subscription
              </Typography>
              <Chip
                label="Free Trial"
                color="default"
                variant="outlined"
              />
            </Box>

            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">
                You're currently on a free trial (1 AWS account). Subscribe through AWS Marketplace to continue with full features.
              </Typography>
            </Alert>

            {trialEndDate && (
              <Box sx={{ mt: 2 }}>
                <Typography variant="body2" color="text.secondary">
                  Free Trial Expires
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 500 }}>
                  {formatDateTime(trialEndDate)}
                </Typography>
                <Typography variant="body2" sx={{ fontWeight: 600, color: 'warning.main', mt: 0.5 }}>
                  {formatTrialRemaining(trialEndDate)}
                </Typography>
              </Box>
            )}
          </CardContent>
        </Card>
      )}

      {!isFixedPlan && (
        <Card sx={{ mb: 4 }}>
          <CardContent>
            <Typography variant="h6" sx={{ fontWeight: 600, mb: 1 }}>
              AWS Marketplace
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
              {isMarketplacePlan
                ? 'Manage your TagOps subscription, invoices, and cancellations in AWS Marketplace.'
                : 'TagOps billing is available through AWS Marketplace. Subscribe there to raise your account limit and keep access after the trial.'}
            </Typography>
            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2 }}>
              {!isMarketplacePlan && (
                <Button
                  variant="contained"
                  color="primary"
                  endIcon={<OpenInNewIcon />}
                  href={AWS_MARKETPLACE_SUBSCRIBE_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  sx={{ textTransform: 'none' }}
                >
                  Subscribe on AWS Marketplace
                </Button>
              )}
              <Button
                variant={isMarketplacePlan ? 'contained' : 'outlined'}
                color="primary"
                endIcon={<OpenInNewIcon />}
                href={AWS_MARKETPLACE_MANAGE_URL}
                target="_blank"
                rel="noopener noreferrer"
                sx={{ textTransform: 'none' }}
              >
                Manage in AWS Marketplace
              </Button>
            </Box>
          </CardContent>
        </Card>
      )}
    </Box>
  );
};
